import React, { useState } from 'react'
import { usePermissions } from '@/hooks/queries/usePermissions'
import { useDeletePermission } from '@/hooks/mutations/permissions'
import { toast } from 'react-hot-toast'
import { useNavigate } from 'react-router-dom'

const headers = [
  {
    name: 'Title',
    styles: 'min-w-[160px]',
    inputType: 'text',
    searchIcon: true,
  },
  { name: '', styles: 'min-w-[100px]' },
]

const Permissions = () => {
  const { data: permissions, isLoading, isError } = usePermissions()
  const [searchTerm, setSearchTerm] = useState('')
  const navigate = useNavigate()
  const deletePermission = useDeletePermission()

  // Filter permissions based on search term
  const filteredPermissions = permissions?.data?.filter(permission => 
    permission.title.toLowerCase().includes(searchTerm.toLowerCase())
  )

  const handleDelete = async (permissionId) => {
    if (window.confirm('Are you sure you want to delete this permission?')) {
      try {
        await deletePermission.mutateAsync(permissionId)
        toast.success('Permission deleted successfully')
      } catch (error) {
        toast.error(error.message || 'Failed to delete permission')
      }
    }
  }

  if (isLoading) return <div>Loading...</div>
  if (isError) return <div>Error loading permissions</div>

  return (
    <section className='bg-gray-2 dark:bg-dark py-5'>
      <div className='container mx-auto'>
        <div className='bg-white rounded-lg dark:bg-dark-2 shadow-card'>
          <TableTop searchTerm={searchTerm} setSearchTerm={setSearchTerm} />
          <div className='max-w-full overflow-x-auto'>
            <table className='w-full table-auto'>
              <TableHead headers={headers} />
              <TableBody 
                data={filteredPermissions} 
                onDelete={handleDelete} 
              />
            </table>
          </div>
        </div>
      </div>
    </section>
  )
}

const TableTop = ({ searchTerm, setSearchTerm }) => {
  const navigate = useNavigate()

  return (
    <div className='flex items-center justify-between border-b border-stroke dark:border-dark-3 py-4 px-5'>
      <div className='w-full md:max-w-[420px]'>
        <input
          type='text'
          placeholder='Search permissions...'
          value={searchTerm}
          onChange={(e) => setSearchTerm(e.target.value)}
          className='h-[46px] w-full rounded-md border dark:border-dark-3 bg-transparent dark:text-dark-6 placeholder:opacity-60 border-stroke pl-5 pr-14 text-base text-body-color outline-none focus:border-primary'
        />
      </div>
      <button 
        onClick={() => navigate('/permissions/create')}
        className='flex h-[46px] items-center justify-center rounded-md bg-primary px-6 text-base font-medium text-white hover:bg-opacity-90'
      >
        Add Permission
      </button>
    </div>
  )
}

const TableHead = ({ headers }) => {
  return (
    <thead>
      <tr className='border-b border-stroke dark:border-dark-3'>
        {headers.map((header, index) => (
          <th key={index} className={`py-3 px-4 text-left text-sm font-medium text-gray-500`}>
            {header.name}
          </th>
        ))}
      </tr>
    </thead>
  )
}

const TableBody = ({ data, onDelete }) => {
  return (
    <tbody>
      {data.map((permission) => (
        <tr key={permission.id} className='border-b border-stroke dark:border-dark-3'>
          <td className='py-5 px-4'>
            <p className='text-base font-medium text-primary'>{permission.title}</p>
          </td>
          <td className='py-5 pl-4 pr-8'>
            <Dropdown 
              permissionId={permission.id}
              onDelete={onDelete}
            />
          </td>
        </tr>
      ))}
    </tbody>
  )
}

// Dropdown component for edit and delete actions
const Dropdown = ({ permissionId, onDelete }) => {
  const [dropdownOpen, setDropdownOpen] = useState(false)
  const navigate = useNavigate()

  return (
    <div className='relative'>

      <button
        onClick={() => setDropdownOpen(!dropdownOpen)}
        className='text-body-color dark:text-dark-6'
      >
        <svg width={24} height={24} viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
          <path fillRule="evenodd" clipRule="evenodd" d="M10 12C10 10.8954 10.8954 10 12 10C13.1046 10 14 10.8954 14 12C14 13.1046 13.1046 14 12 14C10.8954 14 10 13.1046 10 12Z" fill="currentColor"/>
          <path fillRule="evenodd" clipRule="evenodd" d="M10 5C10 3.89543 10.8954 3 12 3C13.1046 3 14 3.89543 14 5C14 6.10457 13.1046 7 12 7C10.8954 7 10 6.10457 10 5Z" fill="currentColor"/>
          <path fillRule="evenodd" clipRule="evenodd" d="M10 19C10 17.8954 10.8954 17 12 17C13.1046 17 14 17.8954 14 19C14 20.1046 13.1046 21 12 21C10.8954 21 10 20.1046 10 19Z" fill="currentColor"/>
        </svg>
      </button>


      <div
        className={`absolute right-0 top-full z-40 w-[200px] space-y-1 rounded bg-white p-2 shadow-card dark:bg-dark border-stroke dark:border-dark-3 border ${
          dropdownOpen ? 'block' : 'hidden'
        }`}
      >
        <button 
          onClick={() => {
            navigate(`/permissions/edit/${permissionId}`);
            setDropdownOpen(false);
          }}
          className='w-full px-3 py-2 text-sm text-left rounded text-body-color hover:bg-gray-2 dark:text-dark-6 dark:hover:bg-dark-2'
        >
          Edit
        </button>
        <button 
          onClick={() => {
            onDelete(permissionId);
            setDropdownOpen(false);
          }}
          className='w-full px-3 py-2 text-sm text-left rounded text-body-color hover:bg-gray-2 dark:text-dark-6 dark:hover:bg-dark-2'
        >
          Delete
        </button>
      </div>
    </div>
  )
}

export default Permissions 